import {
  ActionButton,
  Detail,
  DropdownItem,
  ErrorAlertWithDrawer,
  formatDate,
  GetApiErrorMessage,
  InputDataUploadInfoMetadataType,
  InputDataUploadInfoType,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  Table,
  TextEllipsis,
  useMutateInputDataUploadAction,
  useMutationErrors,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Dropdown, Flex } from 'antd';
import { FC, useMemo } from 'react';
import translations from './InputDataUploadInfoModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { MenuInfo } from 'rc-menu/lib/interface';
import * as Icon from 'react-feather';

const { addTranslations, t, exists } = i18nNsFactory('input-data-upload-info');
addTranslations(translations);

export interface InputDataUploadInfoModalProps {
  uploadInfo: InputDataUploadInfoType;
  onClose: () => void;
}

const columnHelper = createColumnHelper<InputDataUploadInfoMetadataType>();
const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export const InputDataUploadInfoModal: FC<InputDataUploadInfoModalProps> = (
  props,
) => {
  const { uploadInfo, onClose } = props;
  const { mutateAsync, isPending } = useMutateInputDataUploadAction();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const columns = useMemo(
    () => [
      columnHelper.accessor('dataObjectGroupCode', {
        header: t('column.group'),
        cell: (props) => {
          const { dataObjectGroupCode: code, dataObjectGroupName: name } =
            props.row.original;
          if (!code) return null;
          return <TextEllipsis>{`${code} - ${name}`}</TextEllipsis>;
        },
      }),
      columnHelper.accessor('dataObjectDescriptionCode', {
        header: t('column.input'),
        cell: (props) => {
          const {
            dataObjectDescriptionCode: code,
            dataObjectDescriptionName: name,
          } = props.row.original;
          return <TextEllipsis>{`${code} - ${name}`}</TextEllipsis>;
        },
      }),
      columnHelper.accessor((rowData) => formatDate(rowData.valueDate), {
        id: 'valueDate',
        header: t('column.valueDate'),
        cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
        filterFn: 'formattedDate',
        meta: { width: '20ch' },
      }),
      columnHelper.accessor('subjectsCount', {
        header: t('column.subjectCount'),
        enableColumnFilter: false,
        meta: { width: '12ch' },
      }),
    ],
    [],
  );

  const menu = useMemo(
    () => ({
      items: [
        {
          label: <DropdownItem>{t('actions.REPLACE_ALL')}</DropdownItem>,
          key: 'REPLACE_ALL',
        },
        {
          label: (
            <DropdownItem>{t('actions.REPLACE_DATA_OBJECT')}</DropdownItem>
          ),
          key: 'REPLACE_DATA_OBJECT',
        },
        {
          label: <DropdownItem>{t('actions.REPLACE_SUBJECT')}</DropdownItem>,
          key: 'REPLACE_SUBJECT',
        },
      ],
      onClick: (e: MenuInfo) => {
        clearMutationErrors();
        mutateAsync({
          data: {
            action: e.key,
            filePath: uploadInfo.filePath,
            metadata: uploadInfo.metadata,
          },
        })
          .then(() => {
            onClose();
          })
          .catch((apiErrorResponse) => {
            setMutationErrors(apiErrorResponse.response?.data.errors || []);
          });
      },
    }),
    [
      clearMutationErrors,
      mutateAsync,
      onClose,
      setMutationErrors,
      uploadInfo.filePath,
    ],
  );

  return (
    <Modal
      onCancel={onClose}
      width="50rem"
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton type="abort" layoutType="detail" onClick={onClose}>
              {t('cancel')}
            </ActionButton>
            <Dropdown menu={menu} trigger={['click']}>
              <ActionButton type="mainAction" loading={isPending}>
                {t('upload')}
                <Icon.ChevronDown size={18} />
              </ActionButton>
            </Dropdown>
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>
            {t('title', { count: uploadInfo.metadata.length })}
          </TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      {mutationErrors && (
        <ErrorAlertWithDrawer
          errorAction={t('action')}
          validationMessagesMap={mutationErrors}
        />
      )}
      <Padding scrollable>
        <Flex vertical gap="middle">
          <Table
            name="input-data-upload-info-table"
            columns={columns}
            data={uploadInfo.metadata}
            actionsInFront
            actions={
              <Detail key="fileName" label={t('fileName')}>
                {uploadInfo.filePath}
              </Detail>
            }
            defaultSort={[{ id: 'dataObjectDescriptionCode', desc: false }]}
          />
        </Flex>
      </Padding>
    </Modal>
  );
};
